/**
 * Dropdown menu component for exporting lessons to PowerPoint and Word
 */

import React, { useState, useRef, useEffect } from 'react';
import { FileDown, FileText, ChevronDown, Loader2, X } from 'lucide-react';
import { useTranslation } from '../../contexts/TranslationContext';
import { Lesson } from '../../types';
import { exportAndDownloadLesson as exportToPowerPoint, ExportOptions } from '../../services/powerpointExportService';
import { exportAndDownloadLesson as exportToWord, WordExportOptions } from '../../services/wordExportService';
import toast from 'react-hot-toast';

interface ExportDropdownProps {
  lesson: Lesson;
  powerpointOptions?: ExportOptions;
  wordOptions?: WordExportOptions;
  className?: string;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
}

export const ExportDropdown: React.FC<ExportDropdownProps> = ({
  lesson,
  powerpointOptions,
  wordOptions,
  className = '',
  variant = 'default',
  size = 'md'
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [exportType, setExportType] = useState<'word' | 'powerpoint' | null>(null);
  const [isExportingPowerPoint, setIsExportingPowerPoint] = useState(false);
  const [isExportingWord, setIsExportingWord] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Export options state
  const [exportOptions, setExportOptions] = useState({
    includeResources: powerpointOptions?.includeResources ?? wordOptions?.includeResources ?? true,
    includeAssessments: powerpointOptions?.includeAssessments ?? wordOptions?.includeAssessments ?? true,
    includeNotes: powerpointOptions?.includeNotes ?? wordOptions?.includeNotes ?? true,
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close modal when clicking outside
  useEffect(() => {
    const handleModalClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showOptionsModal && target.classList.contains('bg-black')) {
        setShowOptionsModal(false);
        setExportType(null);
      }
    };

    if (showOptionsModal) {
      document.addEventListener('mousedown', handleModalClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleModalClickOutside);
    };
  }, [showOptionsModal]);

  const handlePowerPointClick = () => {
    setExportType('powerpoint');
    setShowOptionsModal(true);
    setIsOpen(false);
  };

  const handleWordClick = () => {
    setExportType('word');
    setShowOptionsModal(true);
    setIsOpen(false);
  };

  const handlePowerPointExport = async () => {
    if (!lesson || !lesson.metadata) {
      toast.error(t('exportPowerPoint.error.noLesson') || 'No lesson data available');
      return;
    }

    setIsExportingPowerPoint(true);
    setShowOptionsModal(false);
    
    const options: ExportOptions = {
      ...powerpointOptions,
      includeResources: exportOptions.includeResources,
      includeAssessments: exportOptions.includeAssessments,
      includeNotes: exportOptions.includeNotes,
    };
    
    try {
      await exportToPowerPoint(lesson, options);
      toast.success(t('exportPowerPoint.success') || 'Lesson exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(
        t('exportPowerPoint.error.exportFailed') || 
        `Failed to export lesson: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setIsExportingPowerPoint(false);
      setExportType(null);
    }
  };

  const handleWordExport = async () => {
    if (!lesson || !lesson.metadata) {
      toast.error(t('exportWord.error.noLesson') || 'No lesson data available');
      return;
    }

    setIsExportingWord(true);
    setShowOptionsModal(false);
    
    const options: WordExportOptions = {
      ...wordOptions,
      includeResources: exportOptions.includeResources,
      includeAssessments: exportOptions.includeAssessments,
      includeNotes: exportOptions.includeNotes,
    };
    
    try {
      await exportToWord(lesson, options);
      toast.success(t('exportWord.success') || 'Lesson exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(
        t('exportWord.error.exportFailed') || 
        `Failed to export lesson: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setIsExportingWord(false);
      setExportType(null);
    }
  };

  const handleConfirmExport = () => {
    if (exportType === 'powerpoint') {
      handlePowerPointExport();
    } else if (exportType === 'word') {
      handleWordExport();
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'px-3 py-1.5 text-sm';
      case 'lg':
        return 'px-6 py-3 text-lg';
      default:
        return 'px-4 py-2 text-base';
    }
  };

  const getVariantClasses = () => {
    switch (variant) {
      case 'outline':
        return 'border-2 border-gray-600 text-gray-600 hover:bg-gray-50';
      case 'ghost':
        return 'text-gray-600 hover:bg-gray-50';
      default:
        return 'bg-gray-600 text-white hover:bg-gray-700';
    }
  };

  const isExporting = isExportingPowerPoint || isExportingWord;

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isExporting}
        className={`
          ${getSizeClasses()}
          ${getVariantClasses()}
          inline-flex items-center gap-2
          rounded-lg font-medium
          transition-colors duration-200
          disabled:opacity-50 disabled:cursor-not-allowed
          focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2
        `}
        title={t('export.dropdown.tooltip') || 'Export lesson'}
      >
        {isExporting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>{t('export.dropdown.exporting') || 'Exporting...'}</span>
          </>
        ) : (
          <>
            <FileDown className="w-4 h-4" />
            <span>{t('export.dropdown.button') || 'Exporter'}</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </>
        )}
      </button>

      {isOpen && !isExporting && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="py-1">
            <button
              onClick={handleWordClick}
              disabled={isExportingWord}
              className="w-full px-4 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileText className="w-4 h-4 text-blue-600" />
              <span>{t('exportWord.button') || 'Export to Word'}</span>
            </button>
            <button
              onClick={handlePowerPointClick}
              disabled={isExportingPowerPoint}
              className="w-full px-4 py-2 text-left text-sm text-red-800 hover:bg-red-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileDown className="w-4 h-4 text-red-800" />
              <span>{t('exportPowerPoint.button') || 'Export to PowerPoint'}</span>
            </button>
          </div>
        </div>
      )}

      {/* Export Options Modal */}
      {showOptionsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('export.options.title') || 'Options d\'export'}
              </h3>
              <button
                onClick={() => {
                  setShowOptionsModal(false);
                  setExportType(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div className="space-y-3">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={exportOptions.includeResources}
                    onChange={(e) => setExportOptions({ ...exportOptions, includeResources: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    {t('export.options.includeResources') || 'Inclure les ressources'}
                  </span>
                </label>
                
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={exportOptions.includeAssessments}
                    onChange={(e) => setExportOptions({ ...exportOptions, includeAssessments: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    {t('export.options.includeAssessments') || 'Inclure les évaluations'}
                  </span>
                </label>
                
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={exportOptions.includeNotes}
                    onChange={(e) => setExportOptions({ ...exportOptions, includeNotes: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    {t('export.options.includeNotes') || 'Inclure les notes'}
                  </span>
                </label>
              </div>
            </div>
            
            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowOptionsModal(false);
                  setExportType(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {t('export.options.cancel') || 'Annuler'}
              </button>
              <button
                onClick={handleConfirmExport}
                disabled={isExportingPowerPoint || isExportingWord}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isExportingPowerPoint || isExportingWord
                  ? (t('export.options.exporting') || 'Exportation...')
                  : (t('export.options.confirm') || 'Exporter')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
